import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback} from "react";
import {useLocation} from "react-router-dom";
import {statusFilterOptionsBasedOnView} from "../../utils/reservation.util";
import {
    RightSidebarRail,
    RightSidebarDivider,
    RightSidebarFullscreenExitButton,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {
    FloorplanReadCanvas,
    FloorplanReadHeaderControls,
} from "./floorPlan/FloorplanReadSurface";
import {floorplanSearchParamsForRestaurant} from "./floorPlan/floorplanState";
import {useFloorplanStatusSelection} from "./floorPlan/hooks/useFloorplanStatusSelection";
import {useFloorplanViewState} from "./floorPlan/hooks/useFloorplanViewState";
import {FloorplanCreateButton} from "./rail/floorplan/FloorplanCreateButton";
import {FloorplanDayPicker} from "./rail/floorplan/FloorplanDayPicker";
import {FloorplanPrintButton} from "./rail/floorplan/FloorplanPrintButton";
import {FloorplanSearchPanel} from "./rail/floorplan/FloorplanSearchPanel";
import {FloorplanStatsButton} from "./rail/floorplan/FloorplanStatsButton";
import {ReservationFullscreenButton} from "./rail/ReservationFullscreenButton";
import {ReservationStatusPicker} from "./rail/ReservationStatusPicker";
import {SingleRestaurantResourcePicker} from "./rail/SingleRestaurantResourcePicker";
import {useReservationRailSearch} from "./rail/search/useReservationRailSearch";
import {useReservationFullscreen} from "./rail/useReservationFullscreen";

const FLOORPLAN_STATUSES = statusFilterOptionsBasedOnView("floorplan");

interface RestaurantFloorplanTabProps {
    sector: Sector;
}

export function RestaurantFloorplanTab({sector}: RestaurantFloorplanTabProps) {
    const {
        date,
        navigateToDate,
        restaurant,
        restaurants,
        selectRestaurant,
    } = useFloorplanViewState(sector);
    const {
        statuses: selectedStatuses,
        setStatuses: setSelectedStatuses,
        initialized: statusSelectionInitialized,
    } = useFloorplanStatusSelection(sector.id!, restaurant?.id);
    const search = useReservationRailSearch();
    const readOnly = useReadOnly();
    const {isFullscreen, setIsFullscreen} = useReservationFullscreen();
    const location = useLocation();
    const openRestaurantInNewTab = useCallback((restaurantId: string) => {
        const nextSearchParams = floorplanSearchParamsForRestaurant(location.search, restaurantId);
        const nextSearch = nextSearchParams.toString();
        const url = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`;

        window.open(url, "_blank", "noopener,noreferrer");
    }, [location.hash, location.pathname, location.search]);

    if (!restaurant || !statusSelectionInitialized) return null;

    return (
        <RightSidebarRailLayout
            sx={theme => ({
                height: {
                    xs: "calc(100dvh - 56px)",
                    md: "calc(100dvh - 108px)",
                },
                "@supports not (height: 100dvh)": {
                    height: isFullscreen
                        ? "100vh"
                        : {
                            xs: "calc(100vh - 56px)",
                            md: "calc(100vh - 108px)",
                        },
                },
                overflow: "hidden",
                ...(isFullscreen && {
                    position: "fixed",
                    inset: 0,
                    width: "100dvw",
                    height: "100dvh",
                    bgcolor: "background.default",
                    overscrollBehavior: "contain",
                    zIndex: theme.zIndex.modal,
                }),
            })}
        >
            <RightSidebarRailHeader compact={isFullscreen}>
                <RightSidebarFullscreenExitButton
                    active={isFullscreen}
                    onExit={() => setIsFullscreen(false)}
                />

                <RightSidebarRailHeaderCenter>
                    <FloorplanReadHeaderControls
                        date={date}
                        onDateChange={navigateToDate}
                    />
                </RightSidebarRailHeaderCenter>
            </RightSidebarRailHeader>

            <FloorplanReadCanvas
                restaurant={restaurant}
                date={date}
                desktopReservationsCollapsible
            />

            <RightSidebarRail>
                    <FloorplanSearchPanel
                        restaurant={restaurant}
                        date={date}
                        statuses={selectedStatuses}
                        search={search}
                    />
                    <ReservationStatusPicker
                        availableStatuses={FLOORPLAN_STATUSES}
                        selectedStatuses={selectedStatuses}
                        onSelectedStatusesChange={setSelectedStatuses}
                    />
                    <FloorplanDayPicker/>
                    <ReservationFullscreenButton
                        isFullscreen={isFullscreen}
                        onChange={setIsFullscreen}
                    />
                    {!readOnly && (
                        <>
                            <RightSidebarDivider/>
                            <FloorplanCreateButton
                                sector={sector}
                                restaurant={restaurant}
                                date={date}
                            />
                            <RightSidebarDivider/>
                        </>
                    )}
                    <SingleRestaurantResourcePicker
                        restaurants={restaurants}
                        selectedRestaurantId={restaurant.id}
                        onSelectedRestaurantIdChange={selectRestaurant}
                        onOpenRestaurantInNewTab={openRestaurantInNewTab}
                    />
                    <FloorplanPrintButton/>
                    <FloorplanStatsButton
                        sectorId={sector.id!}
                        restaurant={restaurant}
                        date={date}
                        statuses={selectedStatuses}
                    />
            </RightSidebarRail>
        </RightSidebarRailLayout>
    );
}
